import { AnimatePresence, motion } from 'framer-motion';
import { Building2, CheckCircle2, FlaskConical, Mic, Send } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { fadeUp, revealOnView, stagger } from '@marketing/motion/variants';

// ============================================================
// PartnershipBlock — 3 colunas + formulário
// ============================================================

const tracks = [
  {
    id: 'sus',
    icon: Building2,
    title: 'Clínicas SUS',
    body:
      'Sua unidade de saúde quer ser piloto? Acompanhamento contínuo de TEA com 3-5 famílias por 4-6 semanas em alfa fechado.',
  },
  {
    id: 'researchers',
    icon: FlaskConical,
    title: 'Pesquisadores',
    body:
      'Tem interesse em integrar-se ao estudo controlado (M8)? 120 pacientes randomizados, instrumentos validados, 24 semanas.',
  },
  {
    id: 'autistas',
    icon: Mic,
    title: 'Conselho consultivo de autistas adultos',
    body:
      'Sua experiência guia o redesign. Encontros mensais remunerados, voz vinculante em decisões UX e ética.',
  },
];

const inquirySchema = z.object({
  name: z.string().min(2, 'Informe seu nome completo (mínimo 2 caracteres).'),
  organization: z.string().min(2, 'Informe organização ou papel.'),
  email: z.string().email('E-mail inválido.'),
  track: z.enum(['sus', 'researchers', 'autistas', 'other']),
  message: z.string().min(20, 'Mensagem muito curta — escreva ao menos 20 caracteres.'),
  consent: z.literal(true, {
    errorMap: () => ({ message: 'Você precisa autorizar o tratamento dos dados.' }),
  }),
});

type InquiryInput = z.infer<typeof inquirySchema>;

export function PartnershipBlock() {
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<InquiryInput>({
    mode: 'onBlur',
  });

  const onSubmit = async (data: InquiryInput) => {
    // Validação Zod manual (sem resolver para minimizar bundle)
    const parsed = inquirySchema.safeParse(data);
    if (!parsed.success) return;

    // MVP: abre cliente de e-mail. Cloud Function `submitPartnershipInquiry`
    // virá em escopo separado.
    const subject = `[Lume — Parceria] ${parsed.data.track} — ${parsed.data.name}`;
    const body = [
      `Nome: ${parsed.data.name}`,
      `Organização/papel: ${parsed.data.organization}`,
      `E-mail: ${parsed.data.email}`,
      `Frente: ${parsed.data.track}`,
      '',
      'Mensagem:',
      parsed.data.message,
    ].join('\n');

    const mailto = `mailto:contato@lume.med.br?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;

    window.open(mailto, '_blank');
    setSubmitted(true);
    reset();
  };

  return (
    <section
      id="parceria"
      className="lume-section lume-section--alt"
      aria-labelledby="partnership-title"
    >
      <div className="lume-container">
        <motion.header
          {...revealOnView}
          variants={stagger(0.08)}
          className="lume-stack lume-stack-md"
          style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)', maxWidth: '52rem' }}
        >
          <motion.p variants={fadeUp} className="lume-eyebrow">Parceria</motion.p>
          <motion.h2 variants={fadeUp} id="partnership-title" className="lume-h2 lume-text-balance">
            Vamos construir juntos a próxima geração de cuidado em TEA
          </motion.h2>
        </motion.header>

        <motion.div
          {...revealOnView}
          variants={stagger(0.08)}
          style={{
            display: 'grid',
            gap: '1.5rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            marginBottom: 'clamp(2rem, 4vw, 3rem)',
          }}
        >
          {tracks.map((t) => {
            const Icon = t.icon;
            return (
              <motion.article key={t.id} variants={fadeUp} className="lume-card">
                <Icon
                  size={28}
                  aria-hidden="true"
                  style={{ color: 'var(--color-warm-700)', marginBottom: '0.85rem' }}
                />
                <h3 className="lume-h3" style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>
                  {t.title}
                </h3>
                <p style={{ margin: 0, color: 'var(--color-fg-muted)', fontSize: '0.92rem' }}>
                  {t.body}
                </p>
              </motion.article>
            );
          })}
        </motion.div>

        <motion.form
          {...revealOnView}
          variants={fadeUp}
          onSubmit={handleSubmit(onSubmit)}
          className="lume-card"
          style={{ maxWidth: '720px', margin: '0 auto' }}
          aria-labelledby="partnership-form-title"
          noValidate
        >
          <h3 id="partnership-form-title" className="lume-h3" style={{ fontSize: '1.1rem' }}>
            Falar com a equipe TEACare
          </h3>
          <p
            style={{
              margin: '0.4rem 0 1.5rem',
              fontSize: '0.875rem',
              color: 'var(--color-fg-muted)',
            }}
          >
            Envie uma mensagem inicial — abriremos seu cliente de e-mail com os campos prontos.
            Resposta em até 5 dias úteis.
          </p>

          <div className="lume-stack lume-stack-md">
            <Field
              id="name"
              label="Nome completo"
              error={errors.name?.message}
            >
              <input
                id="name"
                type="text"
                autoComplete="name"
                {...register('name', { required: true })}
                style={inputStyle}
              />
            </Field>

            <Field
              id="organization"
              label="Organização / papel"
              hint="Clínica, secretaria de saúde, universidade, ou descrição do papel"
              error={errors.organization?.message}
            >
              <input
                id="organization"
                type="text"
                {...register('organization', { required: true })}
                style={inputStyle}
              />
            </Field>

            <Field id="email" label="E-mail institucional" error={errors.email?.message}>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email', { required: true })}
                style={inputStyle}
              />
            </Field>

            <Field
              id="track"
              label="Frente de interesse"
              error={errors.track?.message}
            >
              <select
                id="track"
                {...register('track', { required: true })}
                style={inputStyle}
                defaultValue=""
              >
                <option value="" disabled>
                  Selecione…
                </option>
                <option value="sus">Clínica SUS — quero ser piloto</option>
                <option value="researchers">Pesquisador(a) — estudo controlado M8</option>
                <option value="autistas">Conselho consultivo de autistas adultos</option>
                <option value="other">Outra colaboração</option>
              </select>
            </Field>

            <Field id="message" label="Mensagem" error={errors.message?.message}>
              <textarea
                id="message"
                rows={5}
                {...register('message', { required: true })}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 120 }}
              />
            </Field>

            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.6rem',
                fontSize: '0.875rem',
                color: 'var(--color-fg-muted)',
              }}
            >
              <input type="checkbox" {...register('consent', { required: true })} />
              <span>
                Autorizo o tratamento dos dados deste formulário pelo Programa TEACare conforme a{' '}
                <a href="#footer">Política de Privacidade</a> e a LGPD.
              </span>
            </label>
            {errors.consent ? (
              <p
                role="alert"
                style={{
                  margin: 0,
                  fontSize: '0.825rem',
                  color: 'var(--color-severity-critical-fg)',
                }}
              >
                {errors.consent.message}
              </p>
            ) : null}

            <button
              type="submit"
              className="lume-btn lume-btn--primary"
              disabled={isSubmitting}
              style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}
            >
              <Send size={16} aria-hidden="true" />
              Enviar mensagem
            </button>

            <AnimatePresence>
              {submitted ? (
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  role="status"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: 'var(--color-severity-stable-fg)',
                    fontWeight: 600,
                    margin: 0,
                  }}
                >
                  <CheckCircle2 size={18} aria-hidden="true" />
                  Pronto! Seu cliente de e-mail foi aberto. Caso não tenha sido, escreva para
                  contato@lume.med.br.
                </motion.p>
              ) : null}
            </AnimatePresence>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="lume-stack lume-stack-sm">
      <label
        htmlFor={id}
        style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '0.9rem' }}
      >
        {label}
      </label>
      {children}
      {hint && !error ? (
        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-fg-subtle)' }}>{hint}</p>
      ) : null}
      {error ? (
        <p
          role="alert"
          style={{
            margin: 0,
            fontSize: '0.825rem',
            color: 'var(--color-severity-critical-fg)',
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.7rem 0.9rem',
  fontSize: '0.95rem',
  fontFamily: 'inherit',
  background: 'var(--color-bg-base)',
  color: 'var(--color-fg-default)',
  border: '1.5px solid var(--color-border-strong)',
  borderRadius: '0.6rem',
  transition: 'border-color 150ms, box-shadow 150ms',
};
